import { useEffect, useRef, useState } from 'react';
import { AssetLoader, type PlayerProfile, type SlotDemoScene } from '@goodtime/engine';
import { BIOMES } from '@goodtime/shared';
import { funnelBeacon } from '../api/funnel';
import { claimWorldOffer, fetchWorldOffer } from '../api/wallet';
import { clearOfferLink, readOfferLink } from '../offerLink';

/** Each world's round painting, as on the worlds list. */
const HEROES = import.meta.glob<{ default: string }>('../assets/biome_hero/*.png', { eager: true });

function heroUrl(world: string): string | null {
  return HEROES[`../assets/biome_hero/${world}.png`]?.default ?? null;
}

export interface WorldOfferLinkProps {
  playerId: string | null;
  /** The slot is up, the age check answered and the wallet synced. */
  ready: boolean;
  scene: () => SlotDemoScene | null;
  profile: PlayerProfile;
  /** "Go there": leave the slot for this world's map (index into BIOMES). */
  onGo: (world: number) => void;
}

/**
 * A world offer behind a Facebook post's link (producer 2026-11-04: "What
 * about unlocking a world now and then? Time sensitive offer"): once the
 * slot is ready it asks the server where the player stands and shows it
 * over the slot. Taking it opens the world's games on the server; "Go
 * there" heads to the world's map. Nothing shows for an unknown offer or
 * when offers are switched off. Renders nothing itself.
 */
export function WorldOfferLink({ playerId, ready, scene, profile, onGo }: WorldOfferLinkProps) {
  const [offerId] = useState<string | null>(readOfferLink);
  const started = useRef(false);
  useEffect(() => {
    const slot = scene();
    if (!offerId || started.current || !playerId || !ready || !slot) return;
    started.current = true;
    clearOfferLink();
    void (async () => {
      try {
        const offer = await fetchWorldOffer(playerId, offerId);
        const world = BIOMES.findIndex((b) => b.id === offer.world);
        const alias = `offer:hero:${offer.world}`;
        const src = heroUrl(offer.world);
        if (src) await AssetLoader.shared.loadTextures([{ alias, src }]).catch(() => undefined);
        funnelBeacon('world_offer', { playerId, detail: `${offer.state}:${offerId}` });
        const go = await slot.playWorldOffer(
          {
            name: offer.name,
            games: offer.slots.length,
            startsAt: offer.startsAt,
            endsAt: offer.endsAt,
            state: offer.state,
            claim: async () => {
              try {
                const r = await claimWorldOffer(playerId, offerId);
                // The server has them already: the profile follows, so the maps open them at once.
                if (r.state === 'taken') for (const id of r.slots) profile.unlockSlot(id, 0);
                if (r.claimed) funnelBeacon('world_offer', { playerId, detail: `opened:${offerId}` });
                return r.state;
              } catch {
                return null;
              }
            },
          },
          () => (src ? AssetLoader.shared.getTexture(alias) : null),
        );
        if (go && world >= 0) onGo(world);
      } catch {
        // No offer this time: the game carries on as usual.
      }
    })();
  }, [offerId, playerId, ready, scene, profile, onGo]);
  return null;
}
